import React, { useEffect, useState, useMemo } from "react";
import Input from "./Input";
import { X, MoveLeft, Trash, Pencil, Bookmark, GripVertical, Info } from "lucide-react";
import {
    useUpdateSubProject, useDeleteSubProject, useLoginDetials
} from "../Use-auth";
import {
    DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
    SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../../lib/api";
import ReactQuill from "../ui/RichText";
import Button from "./Button";
import { getStatusLabelColor } from "../Pages/utils/taskUtils";
import TeamDetialis from "./TeamProject";
import ProjectCalender from "./ProjectCalender";
import { getStatusColor } from "../Pages/utils/taskUtils";

function SortableSubProject({ sub, children, currentUser }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: sub._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const canReorder = ["Admin", "Management"].includes(
        currentUser?.department
    );

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="relative group">
            {canReorder && (
                <div
                    {...listeners}
                    className="absolute top-0 left-0 z-10 cursor-grab active:cursor-grabbing 
             bg-[#d4d4d4] 
             hover:bg-black
             rounded-[0.5rem_0_0.5rem_0] 
             p-[2px] transition-all duration-200"
                >
                    <GripVertical className="w-3 h-3 min-[541px]:w-4 min-[541px]:h-4 
                          text-gray-500 hover:text-white" />
                </div>)}
            {children}
        </div>
    );
}

export default function ProjectsPage({
    activeProject,
    newSubProjectName,
    setNewSubProjectName,
    handleAddSubProject,
    setActiveProjectId,
    subProjects = [],
    activeSubProject,
    setActiveSubProject,
    setActiveSubProjectId,
    isEmployee,
    autoClickIdSubProject,
    addingSubProject,
    setSubStatus,
    subStatus,
    setSubProjects,
    currentUser,
    projects,
    activeProjectId,
    activeSubProjectId,
    statuses,
    employees,
    showAddModal,
    setShowAddModal
}) {
    const [editSubProject, setEditSubProject] = useState(null);
    const [editName, setEditName] = useState("");
    const updateSubProjectMutation = useUpdateSubProject();
    const deleteSubProjectMutation = useDeleteSubProject();
    const [status, setStatus] = useState("Pending");
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showTeam, setShowTeam] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        if (activeSubProject) {
            setActiveSubProjectId(activeSubProject);
        }
    }, [activeSubProject]);

    const [orderedProjects, setOrderedProjects] = useState([]);

    useEffect(() => {
        const subs = [
            ...(activeProject?.subProjects || []),
            ...(subProjects || [])
        ];

        const uniqueSubs = Array.from(
            new Map(subs.map(item => [item._id, item])).values()
        );

        const formatted = uniqueSubs
            .filter(Boolean)
            .map((sub, idx) => ({
                ...sub,
                order: sub.order ?? idx + 1
            }));

        setOrderedProjects(formatted);
    }, [activeProject?.subProjects, subProjects]);

    const allSubs = React.useMemo(() => {
        return [
            ...(Array.isArray(activeProject?.subProjects) ? activeProject.subProjects : []),
            ...(Array.isArray(subProjects) ? subProjects : [])
        ]
            .filter(sub => sub && sub._id && sub.projectName)
            .map((sub, idx) => ({
                ...sub,
                order: Number(sub?.order) || idx + 1,
                _id: sub._id || `temp-${idx}`,
            }))
            .sort((a, b) => a.order - b.order);
    }, [activeProject?.subProjects, subProjects]);

    useEffect(() => {
        if (!activeProject?.id) {
            const uniqueSubs = Array.from(
                new Map(allSubs.map(item => [item._id, item])).values()
            );
            setOrderedProjects(uniqueSubs);
            return;
        }

        const saved = localStorage.getItem(`subOrder_${activeProject.id}`);

        const uniqueSubs = Array.from(
            new Map(allSubs.map(item => [item._id, item])).values()
        );

        if (!saved) {
            setOrderedProjects(uniqueSubs);
            return;
        }

        try {
            const ids = JSON.parse(saved);
            const map = new Map(uniqueSubs.map(p => [p._id, p]));

            const sorted = [
                ...ids.map(id => map.get(id)).filter(Boolean),
                ...uniqueSubs.filter(p => !ids.includes(p._id)),
            ];

            setOrderedProjects(sorted);
        } catch {
            setOrderedProjects(uniqueSubs);
        }
    }, [allSubs, activeProject?._id]);

    useEffect(() => {
        if (!autoClickIdSubProject || !allSubs.length) return;

        const sub = allSubs.find(s => s._id === autoClickIdSubProject.toString());
        if (!sub) return;

        setActiveSubProject(sub._id);
        setActiveSubProjectId(sub._id);
        const timeout = setTimeout(() => {
            const el = document.getElementById(sub._id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
                console.warn("Subproject element not found:", sub._id);
            }
        }, 100);

        return () => clearTimeout(timeout);
    }, [autoClickIdSubProject, allSubs, setActiveSubProject, setActiveSubProjectId]);

    const handleDelete = (sub) => {
        if (
            window.confirm(
                `Are you sure you want to delete sub-project "${sub.projectName}"?`
            )
        ) {
            deleteSubProjectMutation.mutate(
                {
                    projectId: activeProject.id,
                    subId: sub._id,
                },
                {
                    onSuccess: () => {
                        setSubProjects(prev =>
                            prev.filter(s => s._id !== sub._id)
                        );

                        if (activeSubProject === sub._id) {
                            setActiveSubProject(null);
                            setActiveSubProjectId(null);
                        }
                    },
                }
            );
        }
    };

    // Handle update submit
    const handleUpdateSubmit = () => {
        if (!editName.trim()) return;

        setLoading(true);

        updateSubProjectMutation.mutate(
            {
                projectId: activeProject._id,
                subId: editSubProject._id,
                subProject: {
                    projectName: editName,
                    status: status,
                    createdBy: editSubProject.createdBy,
                },
            },
            {
                onSuccess: () => {
                    setSubProjects(prev =>
                        prev.map(sp =>
                            sp._id === editSubProject._id
                                ? { ...sp, projectName: editName, status }
                                : sp
                        )
                    );

                    setEditSubProject(null);
                    setEditName("");
                    setLoading(false);
                    setSubStatus("Pending");
                    setStatus("Pending");
                },
            }
        );
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active?.id || !over?.id || active.id === over.id) return;

        setOrderedProjects((items) => {
            const oldIndex = items.findIndex((p) => p.id === active.id);
            const newIndex = items.findIndex((p) => p.id === over.id);

            if (oldIndex === -1 || newIndex === -1) return items;

            const newItems = arrayMove(items, oldIndex, newIndex);

            localStorage.setItem(
                `subOrder_${activeProject.id}`,
                JSON.stringify(newItems.map(p => p.id))
            );

            const projectOrder = newItems.map((p, idx) => ({
                id: p.id,
                order: idx + 1,
            }));

            fetch(api.auth.projects.subOrder.path, {
                method: api.auth.projects.subOrder.method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    projectId: activeProject.id,
                    subProjects: projectOrder,
                }),
            }).catch((err) => console.error("Order update failed", err));

            return newItems;
        });
    };

    useEffect(() => {
        if (!activeProject?._id) return;

        const saved = localStorage.getItem(`subOrder_${activeProject._id}`);
        if (!saved) return;

        try {
            const ids = JSON.parse(saved);

            setOrderedProjects((prev) => {
                const map = new Map(prev.map(p => [p._id, p]));

                return ids
                    .map(id => map.get(id))
                    .filter(Boolean);
            });

        } catch (err) {
            console.error("Failed to restore sub order", err);
        }
    }, [activeProject?._id]);

    const { mutate: updateLoginDetails, isPending } = useLoginDetials();

    const [loginDetails, setLoginDetails] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (activeSubProject && orderedProjects.length) {
            const sub = orderedProjects.find(sp => sp._id === activeSubProject);
            setLoginDetails(sub?.loginDetails || "");
        }
        else if (activeProject) {
            setLoginDetails(activeProject.loginDetials || "");
        }
    }, [activeSubProject, orderedProjects, activeProject]);

    const handleSaveLoginDetails = () => {
        updateLoginDetails(
            {
                projectId: activeProject.id,
                loginDetials: loginDetails,
            },
            {
                onSuccess: () => {
                    setShowModal(false);
                    setIsEditing(false);
                }
            }
        );
    };

    if (!activeProject?.isMultiProject) return null;

    const canReorder = ["Admin", "Management"].includes(
        currentUser?.department
    );

    const statusPriority = {
        "In Progress": 1,
        "Not started": 2,
        "On Hold": 3,
        "Over Due": 4,
        "Completed": 5,
        "Lost": 6,
    };

    return (
        <>
            {!isEmployee && (
                <div className="inline-flex items-start gap-2 justify-between mb-4 w-full">
                    <div className="inline-flex items-start gap-2 ">
                        <div
                            className="inline-flex items-center gap-2 cursor-pointer w-[100%] back-btn"
                            onClick={() => setActiveProjectId(null)}
                        >
                            <MoveLeft size={20} />
                            <span className="font-[400]">{activeProject.projectName}</span>
                        </div>
                        <div
                            className="inline-flex items-center gap-2 bg-zinc-800 text-rose-50 text-xs font-medium px-3 py-1 rounded-full shadow-sm select-none cursor-pointer hover:bg-zinc-700"
                            onClick={() => setShowModal(true)}

                        >
                            Details <Info size={18} />
                        </div>
                    </div>
                    {canReorder && (<div className="hidden sm:flex items-center gap-2">
                        <div
                            className="cursor-pointer inline-flex items-center justify-center rounded-md font-medium transition-all px-3 py-2 sm:px-5 sm:py-2 text-xs sm:text-sm shadow hover:shadow-md bg-blue-600 text-white"
                            onClick={() => setShowCalendar(true)}
                        >
                            Calendar
                        </div>
                        <div
                            className="cursor-pointer inline-flex items-center justify-center rounded-md font-medium transition-all px-3 py-2 sm:px-5 sm:py-2 text-xs sm:text-sm shadow hover:shadow-md bg-blue-600 text-white"
                            onClick={() => setShowTeam(true)}
                        >
                            Team
                        </div>
                    </div>)}
                </div>
            )}


            {showModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-2 sm:px-4"
                    onClick={() => {
                        setShowModal(false);
                        setIsEditing(false);
                    }}
                >
                    <div
                        className="
        relative w-full max-w-3xl
        h-[60vh] sm:h-[75vh]
        bg-white rounded-xl sm:rounded-2xl
        shadow-2xl flex flex-col
        animate-scaleIn
      "
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="
          flex items-center justify-between
          px-3 py-2 sm:px-6 sm:py-4
          border-b bg-gray-50
          rounded-t-xl sm:rounded-t-2xl
        ">
                            <h3 className="
            text-base sm:text-xl
            font-semibold text-gray-800
          ">
                                Project Details
                            </h3>

                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setIsEditing(false);
                                }}
                                className="p-1.5 sm:p-2 rounded-full hover:bg-red-100 transition"
                            >
                                <X size={16} className="sm:w-[18px] sm:h-[18px] text-red-500" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1
          p-3 sm:p-6
          overflow-y-auto
          relative bg-white
        ">
                            {isEditing ? (
                                <ReactQuill
                                    value={loginDetails}
                                    onChange={setLoginDetails}
                                    theme="snow"
                                    placeholder="Enter login details..."
                                    className="
              h-full
              min-h-[250px] sm:min-h-[350px]
              rounded-lg sm:rounded-xl
              border border-gray-300
              text-xs sm:sm
            "
                                />
                            ) : (
                                <div
                                    className="
              h-full
              min-h-[250px] sm:min-h-[350px]
              rounded-lg sm:rounded-xl
              border border-gray-200
              p-3 sm:p-5
              bg-gray-50
              text-gray-700
              text-xs sm:sm
              leading-relaxed
              overflow-auto
              ql-editor
            "
                                    dangerouslySetInnerHTML={{
                                        __html: loginDetails || "<p class='text-gray-400'>No login details</p>",
                                    }}
                                />
                            )}

                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="
              absolute top-3 right-3 sm:top-6 sm:right-6
              p-1.5 sm:p-2
              rounded-lg
              bg-blue-50 hover:bg-blue-100
              transition
            "
                                >
                                    <Pencil
                                        size={14}
                                        className="sm:w-[16px] sm:h-[16px] text-blue-600"
                                    />
                                </button>
                            )}
                        </div>

                        {/* Footer */}
                        {isEditing && (
                            <div className="
            flex justify-end gap-2 sm:gap-3
            px-3 py-2 sm:px-6 sm:py-4
            border-t bg-gray-50
            rounded-b-xl sm:rounded-b-2xl
          ">
                                <Button
                                    onClick={() => setIsEditing(false)}
                                    variant="danger"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    onClick={handleSaveLoginDetails}
                                    disabled={isPending}
                                    variant="success"
                                    className="flex items-center justify-center gap-2"
                                >
                                    {isPending ? (
                                        <>
                                            <span className="loader-circle"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        "Save"
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!isEmployee && (
                <div className="bg-white rounded-lg sm:rounded-2xl shadow-md border border-gray-100 p-2 sm:p-6 w-full">
                    <div className="flex items-end gap-3 flex-wrap justify-end">

                        {/* Search */}
                        <div className="flex-1 max-w-[200px]">
                           
                            <Input
                                type="search"
                                value={search}
                                placeholder="Search projects..."
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full"
                            />
                        </div>

                        {/* Button */}
                        <div>
                            <Button
                                onClick={() => setShowAddModal(true)}
                                variant="default"
                            >
                                Add Project
                            </Button>
                        </div>

                    </div>
                </div>
            )}

            {showAddModal && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        className="bg-white rounded-xl w-[600px] p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold mb-4">Add Sub Project</h3>

                        <div className="mb-3">
                            <label className="text-sm">Sub Project Name</label>
                            <Input
                                value={newSubProjectName}
                                onChange={(e) => setNewSubProjectName(e.target.value)}
                                placeholder="Enter project name"
                            />
                        </div>

                        {/* Status */}
                        <div className="mb-4">
                            <label className="text-sm">Status</label>
                            <select
                                value={subStatus}
                                onChange={(e) => setSubStatus(e.target.value)}
                                className="w-full border rounded px-2 py-2"
                            >
                                <option value="">Select Status</option>
                                <option value="Not started">Not started</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Over Due">Over Due</option>
                                <option value="On Hold">On Hold</option>
                                <option value="Lost">Lost</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-2">
                            <Button variant="danger" onClick={() => setShowAddModal(false)}>
                                Cancel
                            </Button>

                            <Button
                                variant="success"
                                onClick={() => {
                                    handleAddSubProject();
                                }}
                            >
                                {addingSubProject ? "Adding..." : "Add"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {showTeam && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-2 sm:px-4"
                    onClick={() => setShowTeam(false)}
                >
                    <div
                        className="
  relative w-full 
  max-w-[80%] h-[90vh] 

  max-lg:max-w-full 
  max-lg:h-[95vh] 
  max-lg:rounded-t-2xl 
  max-lg:rounded-b-none 
  max-lg:mt-auto

  bg-white rounded-2xl shadow-2xl flex flex-col animate-scaleIn
"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
                            <h3 className="text-lg font-semibold">Team Details</h3>

                            <button
                                onClick={() => setShowTeam(false)}
                                className="p-2 rounded-full hover:bg-red-100"
                            >
                                <X size={18} className="text-red-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4">
                            <TeamDetialis
                                project={projects}
                                activeProjectId={activeProjectId}
                                statuses={statuses}
                                user={currentUser}
                                activeSubProjectId={activeSubProjectId}
                                employees={employees}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showCalendar && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-2 sm:px-4"
                    onClick={() => setShowCalendar(false)}
                >
                    <div
                        className="
  relative w-full 
  max-w-[80%] h-[90vh] 

  max-lg:max-w-full 
  max-lg:h-[95vh] 
  max-lg:rounded-t-2xl 
  max-lg:rounded-b-none 
  max-lg:mt-auto

  bg-white rounded-2xl shadow-2xl flex flex-col animate-scaleIn
"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
                            <h3 className="text-lg font-semibold">Project Calendar</h3>

                            <button
                                onClick={() => setShowCalendar(false)}
                                className="p-2 rounded-full hover:bg-red-100"
                            >
                                <X size={18} className="text-red-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-4">
                            <ProjectCalender
                                project={projects}
                                activeProjectId={activeProjectId}
                                getStatusColor={getStatusColor}
                                activeSubProjectId={activeSubProjectId}
                            />
                        </div>
                    </div>
                </div>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={orderedProjects.map((s) => s._id)}
                    strategy={rectSortingStrategy}
                >
                    {/* GRID */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 mt-5">

                        {orderedProjects
                            .filter((sub) =>
                                sub.projectName
                                    .toLowerCase()
                                    .includes(search.toLowerCase())
                            ).sort((a, b) => {
                                const priorityA = statusPriority[a.status] ?? 999;
                                const priorityB = statusPriority[b.status] ?? 999;

                                if (priorityA !== priorityB) {
                                    return priorityA - priorityB;
                                }

                                return a.order - b.order;
                            })
                            .map((sub, idx) => {
                                const isActive = activeSubProject === sub._id;

                                return (
                                    <SortableSubProject
                                        key={sub._id || idx}
                                        sub={sub}
                                        currentUser={currentUser}
                                    >
                                        {/* CARD */}
                                        <div
                                            onClick={() => setActiveSubProject(sub._id)}
                                            className={`relative group p-4 sm:p-5 rounded-md sm:rounded-xl cursor-pointer
                bg-white border shadow-sm transition-all duration-300
                hover:shadow-lg 
                ${isActive
                                                    ? "ring-2 ring-blue-500 border-blue-300 bg-blue-50"
                                                    : "border-gray-200 hover:bg-gray-50"
                                                }`}
                                        >

                                            {/* ACTIONS */}

                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                                {!isEmployee && (
                                                    <Trash
                                                        size={25}
                                                        className="p-1 rounded text-red-500 cursor-pointer hover:bg-red-100"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(sub);
                                                        }}
                                                    />)}

                                                <Pencil
                                                    size={25}
                                                    className="p-1 rounded text-blue-500 cursor-pointer hover:bg-blue-100"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditSubProject(sub);
                                                        setEditName(sub.projectName);
                                                        setStatus(sub.status || "Pending");
                                                    }}
                                                />

                                            </div>

                                            <div className="font-semibold text-[14px] sm:text-base text-gray-800 truncate mb-[20px]">
                                                {sub.projectName}
                                            </div>

                                            <div
                                                className={`absolute bottom-0 right-0 flex items-center gap-1 
    text-[11px] px-2 py-0.5 font-medium
    rounded-[0.5rem_0_0.5rem_0]
    ${getStatusLabelColor(sub.status)}`}
                                            >
                                                <Bookmark size={12} />
                                                {sub.status || "Pending"}
                                            </div>

                                        </div>
                                    </SortableSubProject>
                                );
                            })}

                    </div>
                </SortableContext>
            </DndContext>

            {editSubProject && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center  p-2 max-[540px]:p-1 z-[99999]" onClick={() => setEditSubProject(null)}>
                    <div className="bg-white rounded-xl shadow-2xl w-[600px] h-[40vh] p-6 relative animate-scaleIn" onClick={(e) => e.stopPropagation()}>
                        <div
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                            onClick={() => setEditSubProject(null)}
                        >
                            <X size={20} />
                        </div>
                        <h3 className="text-xl max-[540px]:text-base font-semibold mb-5 max-[540px]:mb-3 text-center">Edit Sub-Project</h3>
                        <div className="mb-4">
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="Sub Project Name"
                                className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-1 text-sm font-medium text-gray-700">Status: </label>
                            <div>
                                <select name="projectStatus" id="projectStatus"
                                    className="
          w-full border border-gray-300 rounded-lg
          px-3 py-2 
          text-sm max-[540px]:text-xs
          focus:outline-none focus:ring-2 focus:ring-blue-400
        "
                                    value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="">Select Status</option>
                                    {isEmployee ? (
                                        <option value="In Review">In Review</option>
                                    ) : (
                                        <>
                                            <option value="Not started">Not started</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Over Due">Over Due Projects</option>
                                            <option value="On Hold">On Hold projects</option>
                                            <option value="Lost">Lost Projects</option>
                                            <option value="Completed">Completed Projects</option>
                                            <option value="In Review">In Review</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button
                                variant="danger"
                                onClick={() => setEditSubProject(null)}
                            >
                                Cancel
                            </Button>
                            <div className="flex justify-end gap-1">
                                <Button
                                    variant="success"
                                    onClick={handleUpdateSubmit}
                                    disabled={loading}
                                    className="flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    {loading ? (
                                        <>
                                            Updating...
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>

                                        </>
                                    ) : (
                                        "Update"
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
